import * as projection from '@arcgis/core/geometry/projection';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import { Cancel, ContentCopy } from '@mui/icons-material';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import PolylineIcon from '@mui/icons-material/Polyline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Typography } from '@mui/material';
import Fab from '@mui/material/Fab';
import FormControl from '@mui/material/FormControl';
import React, { useEffect, useState } from 'react';
import { emitCustomEvent } from 'react-custom-events';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import Events from '../../defaults/Events';
import FieldNames from '../../defaults/FieldNames.json';
import PolygonIcon from '../../icons/PolygonIcon';
import ErrorService from '../../services/ErrorService';
import { isFeaturePostFire } from '../../utils/FeatureHelper';
import './CreatePerimeterToolbar.css';
import { generateFeatureCollectionFromShapefile } from '../../utils/UploadHelper';
import { setAlertMessageObj } from '../../redux/slices/globalContextSlice';
import { useDispatch } from 'react-redux';

function CreateActualsToolbar({
    onCreatePerimeterCancel,
    onDrawComplete,
    isTreatment,
    isCreatingActualFeature,
    plannedFeature,
    onReplaceNewActualPerimeter,
    geoType,
}) {
    const [showShapeParseError, setShowShapeParseError] = useState(false);
    const inputReaderRef = React.useRef(null);
    const config = useSelector((state) => state.configuration.config);
    const dispatch = useDispatch();

    const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
    const nifcToken = useSelector((state) => state.configuration.nifcToken);

    const uploadShapefile = () => {
        inputReaderRef.current.click();
    };

    const drawNewActualFeature = () => {
        //Can polygon
        emitCustomEvent(Events.EnableDrawMode, {
            geometry: null,
            callback: drawnActualFeatureCompleted,
        });
    };

    const drawNewLineActualFeature = () => {
        //Can be line
        emitCustomEvent(Events.EnablePostFireLineMode, {
            geometry: null,
            callback: drawnActualFeatureCompleted,
        });
    };

    const drawnActualFeatureCompleted = (graphic) => {
        // This is where we populate data for the Actual object. Consider moving to detailsContainer at the start.
        const isPostFire = isFeaturePostFire(plannedFeature?.attributes);
        const plannedObjectID = plannedFeature?.attributes[FieldNames.featureFields.objectID];
        let obj = {
            attributes: null,
            geometry: graphic.toJSON(),
            isPoint: false,
            plannedObjectID: plannedObjectID,
            geoType: geoType,
            isPostFire: isPostFire,
            isTreatment: isTreatment,
        };
        if (geoType === FieldNames.joinFeatureValues.polygon) {
            emitCustomEvent(Events.ActualsPolygonCreated, obj);
        } else if (geoType === FieldNames.joinFeatureValues.line) {
            emitCustomEvent(Events.ActualsLineCreated, obj);
        }

        if (isTreatment) {
            onDrawComplete('treatment');
        } else {
            onDrawComplete('activity');
        }
    };

    const copyActualFeature = () => {
        //Could be any type of geometry type - Polygon, Line, Point
        projection
            .load()
            .then(function () {
                const originGeometry = projection.project(
                    plannedFeature?.geometry,
                    new SpatialReference({ wkid: 4269 })
                );
                const isPoint =
                    plannedFeature?.attributes[FieldNames.featureFields.isPoint] ===
                    FieldNames.yesNo.yes;
                const isPostFire = isFeaturePostFire(plannedFeature?.attributes);
                const plannedObjectID =
                    plannedFeature?.attributes[FieldNames.featureFields.objectID];
                let obj = {
                    attributes: null,
                    geometry: originGeometry,
                    isPoint: isPoint,
                    plannedObjectID: plannedObjectID,
                    geoType: geoType,
                    isPostFire: isPostFire,
                    isTreatment: isTreatment,
                };
                if (geoType === FieldNames.joinFeatureValues.polygon) {
                    emitCustomEvent(Events.ActualsPolygonCreated, obj);
                } else if (geoType === FieldNames.joinFeatureValues.line) {
                    emitCustomEvent(Events.ActualsLineCreated, obj);
                } else if (geoType === FieldNames.joinFeatureValues.point) {
                    emitCustomEvent(Events.ActualsPointCreated, obj);
                }

                if (isTreatment) {
                    onDrawComplete('treatment');
                } else {
                    onDrawComplete('activity');
                }
            })
            .catch((error) => {
                ErrorService.addErrorLogEntry('SYSTEM', error);
            });
    };

    const shapefileChanged = async (e) => {
        const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
        if (file) {
            generateFeatureCollection(file);
        }
        inputReaderRef.current.value = '';
    };

    useEffect(() => {
        if (showShapeParseError) {
            dispatch(
                setAlertMessageObj({
                    message: 'Shapefile Parse Error',
                    severity: 'error',
                    child: (
                        <Typography sx={{ marginLeft: '5px', marginRight: '5px' }}>
                            Unable to parse shapefile or found empty geometry. Please verify .zip
                            contents and geometry and try again.
                        </Typography>
                    ),
                    callback: null,
                })
            );
        }
        // Cleanup function to reset showShapeParseError to false
        return () => {
            setShowShapeParseError(false);
        };
    }, [showShapeParseError, setAlertMessageObj]);

    const generateFeatureCollection = (file) => {
        return generateFeatureCollectionFromShapefile(file, config)
            .then((perimeter) => {
                if (perimeter) {
                    projection.load().then(function () {
                        const originGeometry = projection.project(
                            perimeter,
                            new SpatialReference({ wkid: 4269 })
                        );
                        const isPoint =
                            plannedFeature.attributes[FieldNames.featureFields.isPoint] ===
                            FieldNames.yesNo.yes;
                        const isPostFire = isFeaturePostFire(plannedFeature?.attributes);
                        const plannedObjectID =
                            plannedFeature?.attributes[FieldNames.featureFields.objectID];
                        let obj = {
                            geometry: originGeometry,
                            isPoint: isPoint,
                            plannedObjectID: plannedObjectID,
                            geoType: geoType,
                            isPostFire: isPostFire,
                            isTreatment: isTreatment,
                        };
                        emitCustomEvent(Events.ActualsPolygonCreated, obj);

                        if (isTreatment) {
                            onDrawComplete('treatment');
                        } else {
                            onDrawComplete('activity');
                        }
                    });
                    setShowShapeParseError(false);
                }
            })
            .catch((err) => {
                setShowShapeParseError(true);
            });
    };

    const selectNewActualsFeaturePoint = () => {
        //Can be both a post fire point and a fuels point(really a polygon)
        emitCustomEvent(Events.ToggleIsMovingPoint, false);
        emitCustomEvent(Events.EnablePointMode, {
            geometry: null,
            callback: pointActualsFeatureCompleted,
        });
    };

    const selectNewActualsPostFireFeaturePoint = () => {
        emitCustomEvent(Events.ToggleIsMovingPoint, false);
        emitCustomEvent(Events.EnablePostFirePointMode, {
            geometry: null,
            callback: postFirePointActualsFeatureCompleted,
        });
    };

    const pointActualsFeatureCompleted = (graphic) => {
        //Point only - Fuels Point so it's really a Polygon
        projection.load().then(function () {
            const originGeometry = projection.project(
                graphic,
                new SpatialReference({ wkid: 4269 })
            );
            const isPostFire = isFeaturePostFire(plannedFeature?.attributes);
            const plannedObjectID = plannedFeature?.attributes[FieldNames.featureFields.objectID];
            let obj = {
                attributes: null,
                geometry: originGeometry,
                isPoint: true,
                plannedObjectID: plannedObjectID,
                geoType: geoType,
                isPostFire: isPostFire,
                isTreatment: isTreatment,
            };
            emitCustomEvent(Events.ActualsPolygonCreated, obj);

            if (isTreatment) {
                onDrawComplete('treatment');
            } else {
                onDrawComplete('activity');
            }
        });
    };

    const postFirePointActualsFeatureCompleted = (graphic) => {
        //Point only
        projection.load().then(function () {
            const originGeometry = projection.project(
                graphic,
                new SpatialReference({ wkid: 4269 })
            );
            const isPostFire = isFeaturePostFire(plannedFeature?.attributes);
            const plannedObjectID = plannedFeature?.attributes[FieldNames.featureFields.objectID];
            let obj = {
                attributes: null,
                geometry: originGeometry,
                isPoint: true,
                plannedObjectID: plannedObjectID,
                geoType: geoType,
                isPostFire: isPostFire,
                isTreatment: isTreatment,
            };
            emitCustomEvent(Events.ActualsPointCreated, obj);

            if (isTreatment) {
                onDrawComplete('treatment');
            } else {
                onDrawComplete('activity');
            }
        });
    };

    return ReactDOM.createPortal(
        <div
            className='edit-geometry-container edit-geometry-selector'
            id='EditGeometryToolbarAnchorEl'
        >
            {isCreatingActualFeature &&
                plannedFeature?.attributes &&
                plannedFeature?.attributes[FieldNames.featureFields.isPoint] !==
                    FieldNames.yesNo.yes &&
                geoType === FieldNames.joinFeatureValues.polygon && (
                    <Fab
                        variant='extended'
                        size='medium'
                        color='primary'
                        aria-label='Upload'
                        onClick={() => uploadShapefile()}
                    >
                        <UploadFileIcon sx={{ mr: 1 }} />
                        Upload
                    </Fab>
                )}

            {(isTreatment ||
                (plannedFeature?.attributes &&
                    plannedFeature.attributes[FieldNames.featureFields.isPoint] !==
                        FieldNames.yesNo.yes)) &&
                geoType === FieldNames.joinFeatureValues.polygon && (
                    <Fab
                        style={{ padding: '0 10px', marginLeft: 3, marginBottom: 3 }}
                        variant='extended'
                        size='medium'
                        color='primary'
                        aria-label='Copy'
                        onClick={onReplaceNewActualPerimeter}
                    >
                        <FindReplaceIcon sx={{ mr: 1 }} />
                        Copy
                    </Fab>
                )}

            {isCreatingActualFeature &&
                plannedFeature &&
                plannedFeature.attributes &&
                plannedFeature.attributes[FieldNames.featureFields.isPoint] !==
                    FieldNames.yesNo.yes &&
                geoType === FieldNames.joinFeatureValues.line && (
                    <Fab
                        variant='extended'
                        size='medium'
                        color='primary'
                        aria-label='Draw'
                        onClick={() => drawNewLineActualFeature()}
                    >
                        <PolylineIcon sx={{ mr: 1 }} />
                        Line
                    </Fab>
                )}

            {isCreatingActualFeature &&
                plannedFeature &&
                plannedFeature.attributes &&
                plannedFeature.attributes[FieldNames.featureFields.isPoint] !==
                    FieldNames.yesNo.yes &&
                geoType === FieldNames.joinFeatureValues.polygon && (
                    <Fab
                        variant='extended'
                        size='medium'
                        color='primary'
                        aria-label='Draw'
                        onClick={() => drawNewActualFeature()}
                    >
                        <PolygonIcon sx={{ mr: 1 }} />
                        Polygon
                    </Fab>
                )}

            {/* Fuels Feature Point */}
            {isCreatingActualFeature &&
                plannedFeature.attributes &&
                plannedFeature.attributes[FieldNames.featureFields.isPoint] ===
                    FieldNames.yesNo.yes &&
                geoType === FieldNames.joinFeatureValues.polygon && (
                    <Fab
                        variant='extended'
                        size='medium'
                        color='primary'
                        aria-label='Point'
                        onClick={() => selectNewActualsFeaturePoint()}
                    >
                        <GpsFixedIcon sx={{ mr: 1 }} />
                        Point
                    </Fab>
                )}

            {/* Post-Fire Feature Point */}
            {isCreatingActualFeature &&
                plannedFeature.attributes &&
                plannedFeature.attributes[FieldNames.featureFields.isPoint] ===
                    FieldNames.yesNo.yes &&
                geoType === FieldNames.joinFeatureValues.point && (
                    <Fab
                        variant='extended'
                        size='medium'
                        color='primary'
                        aria-label='Point'
                        onClick={() => selectNewActualsPostFireFeaturePoint()}
                    >
                        <GpsFixedIcon sx={{ mr: 1 }} />
                        Point
                    </Fab>
                )}

            {isCreatingActualFeature && (
                <Fab
                    variant='extended'
                    size='medium'
                    color='primary'
                    aria-label='Draw'
                    onClick={() => copyActualFeature()}
                >
                    <ContentCopy sx={{ mr: 1 }} />
                    Copy As Is
                </Fab>
            )}

            <Fab
                variant='extended'
                size='medium'
                color='primary'
                aria-label='Draw'
                onClick={() => onCreatePerimeterCancel('actual')}
            >
                <Cancel sx={{ mr: 1 }} />
                Cancel
            </Fab>

            <FormControl>
                <input
                    type='file'
                    ref={inputReaderRef}
                    accept='.zip'
                    multiple={false}
                    style={{ display: 'none' }}
                    onChange={(e) => shapefileChanged(e)}
                />
            </FormControl>
        </div>,
        document.getElementById('editPerimeterAnchorEl')
    );
}

export default CreateActualsToolbar;
